import { Controller } from "@hotwired/stimulus"
import { isValueChangingKey } from "../keyboard";

export default class extends Controller {
  static targets = [
    'input',
    'results',
    'idField',
    'nameField',
    'autocomplete'
  ];

  connect() {
    // Prevent Enter key from submitting the form if autosubmit is enabled
    // and no valid selection has been made
    if (this.element.getAttribute("data-autosubmit") == "true") {
      this.inputTarget.addEventListener('keydown', this.handleKeydown.bind(this));
    }
  }

  handleKeydown(e) {
    // If user presses Enter but hasn't selected a valid option, prevent form submission
    if (e.key === 'Enter' && !this.idFieldTarget.value) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  clear() {
    // We call this function whenever autocomplete fetches new results, because
    // this indicates that the user is actively looking for a new value to pick,
    // so meanwhile we should clear any previously set values from the ID and
    // Name hidden fields.
    //
    // However, for autosubmit forms, if a valid selection was already made,
    // we don't want a race condition (late-arriving search) to clear it.
    // Autosubmit forms submit immediately after selection, so if we're here
    // with a selection already made, it's likely a race condition we should ignore.
    const isAutosubmit = this.element.getAttribute("data-autosubmit") == "true";
    if (isAutosubmit && this.idFieldTarget.value) {
      // Selection already made on autosubmit form - don't clear it
      return;
    }

    this.idFieldTarget.value = '';
    this.nameFieldTarget.value = '';

    this.triggerChangeEvent();
  }

  fetch(e) {
    // The underlying autocomplete component is hard-wired to NOT fetch results
    // if the input is empty.  But we sometimes want that behavior, to show the
    // user options as soon as they click into the box (or if they clear the box).
    // So, we force it here.

    if (this.inputTarget.value.trim() !== '') {
      // The autocomplete controller handles non-empty input.
      return;
    }

    // If the input field is empty, ensure the id field is cleared.
    this.idFieldTarget.value = '';

    // In some cases (e.g. location picker) we don't want to show any results
    // until the user types at least one letter.
    const minChars = parseInt(this.element.getAttribute("data-min-chars"));
    if (minChars > 0) {
      return;
    }

    // If it's a keystroke that doesn't change the text query, do nothing.  This
    // could be a user hitting up/down arrows to navigate results.
    if (e.key && !isValueChangingKey(e.which)) {
      return;
    }

    const controller = this.application.getControllerForElementAndIdentifier(this.autocompleteTarget, "autocomplete");
    controller.fetchResults('');
  }

  change(e) {
    if (e.detail.value == "not-pickable") {
      // If the selected option is not meant to be pickable, do nothing.
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
    } else if (e.detail.value == "help") {
      // If the selected option is "help", open Front chat via the shared gate.
      // Chat is limited to members/partners/admins; ineligible visitors are sent to
      // the help center, and eligible clicks during SDK load are queued (see _front).
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
      if (window.goodbillOpenChat) {
        window.goodbillOpenChat();
      } else {
        window.location.href = "https://help.goodbill.com/";
      }
    } else if (e.detail.value.indexOf("inline:") == 0) {
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
      var p = e.detail.value.split("inline:")[1].trim();
      Turbo.visit(p);
    } else if (e.detail.value.indexOf("drawer:") == 0) {
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
      var event = new Event("drawer.open");
      event.url = e.detail.value.split("drawer:")[1].trim();

      // If there's a "context" attribute, pass it along.
      var context = this.element.getAttribute("data-context");
      if (context.length > 0) {
        event.context = context;
      }

      document.dispatchEvent(event);
    } else if (e.detail.value.indexOf('http') == 0 || e.detail.value.indexOf("/") == 0) {
      // If the selected option is a link, open it in a different tab.
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
      window.open(e.detail.value, '_blank');
    } else {
      // Copy the ID and Name of the selected item to the hidden inputs.
      // When the option contains a structured label with an 'option-heading',
      // extract just the heading text (e.g. provider name without address).
      this.idFieldTarget.value = e.detail.value;
      if (e.detail.selected.innerHTML.indexOf('option-heading') >= 0) {
        const headingText = $(e.detail.selected.innerHTML)[0].innerText;
        this.nameFieldTarget.value = headingText;
        this.inputTarget.value = headingText;
      } else {
        this.nameFieldTarget.value = e.detail.textValue;
        this.inputTarget.value = e.detail.textValue;
      }

      // If desired, submit the form (only if a valid selection was made).
      if (this.element.getAttribute("data-autosubmit") == "true" && this.idFieldTarget.value) {
        this.element.closest("form").requestSubmit();
      }
    }

    this.triggerChangeEvent();
  }

  triggerChangeEvent() {
    // Changes to the hidden element don't automatically trigger a change event,
    // so we have to trigger it manually.  Moreover, it has to be done as a DOM
    // event, not as a jQuery event.
    this.idFieldTarget.dispatchEvent(new Event("input"));
    this.idFieldTarget.dispatchEvent(new Event("change"));
    this.nameFieldTarget.dispatchEvent(new Event("input"));
    this.nameFieldTarget.dispatchEvent(new Event("change"));
  }
}